import {
  isContactNoteStageWrite,
  nativeEnvelopeMatches,
} from '../planner/planner-first-contact-note-stage.js';
import { z } from 'zod';
import {
  plannerReadSelectionSchema,
  isPlannerReadSelectionEnvelope,
  type PlannerReadSelection,
} from '../planner/planner-first-read-selection.js';
import type { ChatContextRef } from '@govard/ai-chat-kit';
import { moneyAmountSchema } from '../amount-schema.js';
import {
  plannerWriteSelectionSchema,
  type PlannerWriteSelection,
} from '../planner/planner-first-writes.js';
import {
  intentEnvelopeV1Schema,
  type IntentEnvelopeV1,
  type IntentOperation,
} from '../planner/intent-envelope.js';

const id = z.string().trim().min(1).max(200);
const name = z.string().trim().min(1).max(200);
const fields = {
  title: name,
  description: z.string().trim().min(1).max(1000).optional(),
  dueAt: z.string().trim().min(1).max(200).optional(),
};
const relations = {
  contactId: id.optional(),
  contactName: name.optional(),
  companyId: id.optional(),
  companyName: name.optional(),
};
const uniqueRelation = (params: Record<string, unknown>) =>
  !(params.contactId && params.contactName) && !(params.companyId && params.companyName);

/** No IDs, versions or tool arguments may be supplied through an opaque payload. */
export const SIMPLE_PLAN_COMMAND_SCHEMAS = {
  create_task: z
    .object({ ...fields, ...relations })
    .strict()
    .refine(uniqueRelation)
    .refine(
      (p) =>
        Object.values(p).reduce<number>((n, v) => n + (v === undefined ? 0 : String(v).length), 0) +
          String(p.contactId ?? p.contactName ?? '').length +
          String(p.companyId ?? p.companyName ?? '').length <=
        1950,
    ),
  update_task: z
    .object({
      taskId: id.optional(),
      taskName: name.optional(),
      title: name.optional(),
      description: fields.description,
      dueAt: fields.dueAt,
      status: z.enum(['todo', 'in_progress', 'blocked', 'done', 'cancelled']).optional(),
    })
    .strict()
    .refine((p) => !(p.taskId && p.taskName))
    .refine((p) =>
      ['title', 'description', 'dueAt', 'status'].some(
        (key) => p[key as keyof typeof p] !== undefined,
      ),
    ),
  create_deal: z
    .object({
      title: name,
      amount: z
        .union([
          z.number().refine((value) => moneyAmountSchema.safeParse(value).success),
          z.string().trim().min(1).max(100),
        ])
        .optional(),
      ...relations,
    })
    .strict()
    .refine(uniqueRelation),
  search_contacts: z.object({ query: name }).strict(),
  read_contact: z
    .object({ contactId: id.optional(), name: name.optional() })
    .strict()
    .refine((p) => !(p.contactId && p.name)),
  search_companies: z.object({ query: name }).strict(),
  read_company: z
    .object({ companyId: id.optional(), name: name.optional() })
    .strict()
    .refine((p) => !(p.companyId && p.name)),
} as const;

export type SimplePlanCommandId = keyof typeof SIMPLE_PLAN_COMMAND_SCHEMAS;
export interface SimpleCommandFacts {
  readonly commandId: SimplePlanCommandId;
  readonly params: Record<string, unknown>;
}
export interface SimplePlanInput {
  readonly envelope: IntentEnvelopeV1;
  readonly source: string;
  readonly commandFacts?: SimpleCommandFacts;
  readonly plannerWriteSelection?: PlannerWriteSelection;
  readonly plannerReadSelection?: PlannerReadSelection;
}

/** Internal source evidence in an existing ActionDraft revision, not an execution format. */
export const simplePlanClarificationSourceSchema = z
  .object({
    originalUserMessageId: id,
    clarificationQuestionMessageId: id,
    source: z.string().min(1).max(4000),
    plannerWriteSelection: plannerWriteSelectionSchema.optional(),
    plannerReadSelection: plannerReadSelectionSchema.optional(),
    envelope: z.custom<IntentEnvelopeV1>(
      (value) =>
        value !== null &&
        typeof value === 'object' &&
        'operations' in value &&
        Array.isArray(value.operations),
    ),
    commandFacts: z
      .object({
        commandId: z.enum([
          'create_task',
          'update_task',
          'create_deal',
          'search_contacts',
          'read_contact',
          'search_companies',
          'read_company',
        ]),
        params: z.record(z.unknown()),
      })
      .strict()
      .optional(),
    referenceTime: z.string().datetime({ offset: true }),
    timeZone: z.string().min(1).max(100).optional(),
  })
  .strict()
  .refine((source) => {
    const parsed = intentEnvelopeV1Schema.safeParse(source.envelope);
    if (source.plannerReadSelection)
      return (
        source.plannerWriteSelection === undefined &&
        parsed.success &&
        isPlannerReadSelectionEnvelope(parsed.data, source.plannerReadSelection)
      );
    if (parsed.success && isSimplePlanEnvelope(parsed.data)) return true;
    const write = source.plannerWriteSelection?.write;
    return (
      write !== undefined &&
      isContactNoteStageWrite(write) &&
      nativeEnvelopeMatches(source.envelope, write)
    );
  }, 'Clarification requires a validated simple or typed planner write source')
  .refine(
    (source) =>
      source.commandFacts === undefined ||
      (intentEnvelopeV1Schema.safeParse(source.envelope).success &&
        verifySimpleCommandOperation(source.commandFacts, source.envelope.operations[0]!)
          ?.source === source.source),
    'Clarification source must match strictly validated command facts',
  );

export function isSimplePlanCommand(id: string): id is SimplePlanCommandId {
  return Object.hasOwn(SIMPLE_PLAN_COMMAND_SCHEMAS, id);
}

export function isSimplePlanEnvelope(envelope: IntentEnvelopeV1): boolean {
  const op = envelope.operations[0];
  return (
    envelope.operations.length === 1 &&
    op !== undefined &&
    op.dependsOn.length === 0 &&
    ((op.entity === 'task' && ['create', 'update'].includes(op.operation)) ||
      (op.entity === 'deal' && op.operation === 'create') ||
      (['contact', 'company'].includes(op.entity) && ['search', 'read'].includes(op.operation)))
  );
}

/** Literal, schema-validated values are evidence; the localized label is never parsed. */
export function commandToSimplePlan(
  command: SimpleCommandFacts,
  refs: readonly ChatContextRef[] = [],
): SimplePlanInput {
  const params = SIMPLE_PLAN_COMMAND_SCHEMAS[command.commandId].parse(command.params) as Record<
    string,
    unknown
  >;
  const entity = command.commandId.includes('task')
    ? 'task'
    : command.commandId.includes('deal')
      ? 'deal'
      : command.commandId.includes('compan')
        ? 'company'
        : 'contact';
  const action = command.commandId.startsWith('create_')
    ? 'create'
    : command.commandId.startsWith('update_')
      ? 'update'
      : command.commandId.startsWith('search_')
        ? 'search'
        : 'read';

  const quoted = (kind: 'task' | 'contact' | 'company', text: string) => ({
    entity: kind,
    source: 'user_quoted' as const,
    displayName: text,
    sourceQuote: text,
  });
  const targetName = params.taskName ?? params.name ?? params.query;
  const target =
    action === 'create'
      ? null
      : typeof targetName === 'string'
        ? quoted(entity as 'task' | 'contact' | 'company', targetName)
        : { entity, source: 'trusted_context' as const };
  const operationFields = ['title', 'description', 'dueAt', 'amount', 'status'].flatMap((field) => {
    const value = params[field];
    return value === undefined
      ? []
      : [{ field, rawValue: String(value), sourceQuote: String(value) }];
  });
  const operationRelations = (['contact', 'company'] as const).flatMap<
    IntentOperation['relations'][number]
  >((kind) => {
    if (action !== 'create') return [];
    const named = params[`${kind}Name`];
    const explicitId = params[`${kind}Id`];
    if (typeof named === 'string')
      return [{ field: `${kind}Id`, target: quoted(kind, named), sourceQuote: named }];
    if (typeof explicitId === 'string' || refs.some((r) => r.type === kind))
      return [
        {
          field: `${kind}Id`,
          target: { entity: kind, source: 'trusted_context' as const },
          sourceQuote: typeof explicitId === 'string' ? explicitId : kind,
        },
      ];
    return [];
  });
  const source = [
    command.commandId,
    ...Object.values(params).map(String),
    ...operationRelations.map((r) => r.sourceQuote),
    ...(target?.source === 'trusted_context' ? [entity] : []),
  ].join('\n');
  const envelope = intentEnvelopeV1Schema.parse({
    version: 1,
    mode: action === 'read' || action === 'search' ? 'crm_read' : 'crm_write',
    confidence: 'high',
    missingInformation: [],
    unsupportedRequirements: [],
    operations: [
      {
        clientOperationId: 'simple-op-1',
        entity,
        operation: action,
        target,
        fields: operationFields,
        relations: operationRelations,
        dependsOn: [],
        sourceQuote: source,
      },
    ],
  });
  return { envelope, source, commandFacts: { commandId: command.commandId, params } };
}

/** A separately supplied command authority cannot authorize an altered envelope. */
export function verifySimpleCommandOperation(
  facts: SimpleCommandFacts,
  operation: IntentOperation,
): SimplePlanInput | null {
  try {
    // Context relations in an envelope carry only their kinds, never client IDs.
    const refs = operation.relations
      .filter((r) => r.target.source === 'trusted_context')
      .map((r) => ({ type: r.target.entity, id: 'context', label: 'context' })) as ChatContextRef[];
    const plan = commandToSimplePlan(facts, refs);
    return JSON.stringify(plan.envelope.operations[0]) === JSON.stringify(operation) ? plan : null;
  } catch {
    return null;
  }
}
